import { Separator } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import React from 'react';
import { VesselAdvancedVessel } from '@ais-ui/api-spec/shipdb';

interface EngineItemProps {
  label: string;
  value: string;
}

const EngineInformationItem = ({ label, value }: EngineItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

interface TonnageItemProps {
  label: string;
  value: string;
}

const TonnageInformationItem = ({ label, value }: TonnageItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

interface MiscellaneousItemProps {
  label: string;
  value: string;
}

const MiscellaneousInformationItem = ({
  label,
  value,
}: MiscellaneousItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs select-text">
        {isNonEmpty(value) ? value : '-'}
      </span>
    </div>
  );
};

const Engine = ({ advancedVessel }: { advancedVessel: any }) => {
  const { messages } = useLocalization();
  return (
    <div className="grid grid-cols-2 gap-1">
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEngineTypeLabel}
        value={advancedVessel?.engineType}
      />
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEnginePropulsionPowerLabel}
        value={advancedVessel?.enginePropulsionPowerKw}
      />
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEnginePropulsionTypeLabel}
        value={advancedVessel?.enginePropulsionType}
      />
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEnginePropulsionMethodLabel}
        value={advancedVessel?.enginePropulsionMethod}
      />
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEngineMakerLabel}
        value={advancedVessel?.engineMaker}
      />
      <EngineInformationItem
        label={messages.shipDetailsAdvancedEngineYearLabel}
        value={advancedVessel?.engineYear}
      />
    </div>
  );
};

const Tonnage = ({ advancedVessel }: { advancedVessel: any }) => {
  const { messages } = useLocalization();
  return (
    <div className="grid grid-cols-2 gap-1">
      <TonnageInformationItem
        label={messages.shipDetailsAdvancedDeadWeightTonnageLabel}
        value={
          advancedVessel?.deadweightTonnage
            ? `${advancedVessel.deadweightTonnage} tonnes`
            : '-'
        }
      />
      <TonnageInformationItem
        label={messages.shipDetailsAdvancedGrossTonnageLabel}
        value={
          advancedVessel?.grossTonnage
            ? `${advancedVessel.grossTonnage} GT`
            : '-'
        }
      />
      <TonnageInformationItem
        label={messages.shipDetailsAdvancedNetTonnageLabel}
        value={
          advancedVessel?.netTonnage ? `${advancedVessel.netTonnage} NT` : '-'
        }
      />
    </div>
  );
};

const Miscellaneous = ({ advancedVessel }: { advancedVessel: any }) => {
  const { formatDateWithTimeZone } = useDateTimeFormat();
  const { messages } = useLocalization();
  return (
    <div className="grid grid-cols-2 gap-1">
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedDisplacementLabel}
        value={advancedVessel?.displacement}
      />
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedFormerVesselNameLabel}
        value={advancedVessel?.formerVesselName}
      />
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedBuildPlaceLabel}
        value={advancedVessel?.buildPlace}
      />
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedBuildYearLabel}
        value={advancedVessel?.buildYear}
      />
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedBuilderLabel}
        value={advancedVessel?.builder}
      />
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedKeelLaidDateLabel}
        value={formatDateWithTimeZone(advancedVessel?.keelLaidDate)}
      />
      <MiscellaneousInformationItem
        label={messages.shipDetailsAdvancedVesselMaterialLabel}
        value={advancedVessel?.vesselMaterial}
      />
    </div>
  );
};

export interface AdvancedVesselInformationViewProps {
  advancedVessel?: VesselAdvancedVessel;
}

export const AdvancedVesselInformationView = ({
  advancedVessel,
}: AdvancedVesselInformationViewProps) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Engine</h3>
          <Separator className="mt-2" />
        </div>
        <Engine advancedVessel={advancedVessel} />
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Tonnage</h3>
          <Separator className="mt-2" />
        </div>
        <Tonnage advancedVessel={advancedVessel} />
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Miscellaneous</h3>
          <Separator className="mt-2" />
        </div>
        <Miscellaneous advancedVessel={advancedVessel} />
      </div>
    </div>
  );
};
